import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { GetAllClaimsPayload } from '@/domains/claims/store/types/AllClaimsState';
import type { AllClaimsListResponse } from '@/domains/claims/types/api/responses/AllClaimsListResponse';
import type { EDSAdapterServiceNormalizeConfiguration } from '@/features/eds/services/types/EDSAdapterServiceInjectionConfig';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { AllClaimSetsSlots } from '@/domains/claimSets/enums/AllClaimSetsSlots';
import { updateAllClaimSetsCountersSuccess } from '@/domains/claimSets/store/allClaimSetsListSlice';
import {
  getAllClaimsList,
  getAllClaimsListSuccess,
  incrementAllClaimsListPage,
  resetAllClaimsListSlice,
  updateAllClaimsCounters,
  updateAllClaimsCountersSuccess,
} from '@/domains/claims/store/allClaimsSlice';
import { updateCustomInboxCounter } from '@/domains/user/store/userSlice';
import { MyClaimsTabs } from '@/enums/MyClaimsTabs';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import RecordService from '@/services/RecordService/RecordService';

function* updateMyClaimsCountersSaga() {
  yield delay(100);
  const userId = yield select((state: RootState) => state.userSlice.user?.personId);
  try {
    const paginationParams = QueryParamsService.constructSearchParamsFromRecord({ page: 1, perPage: 1 });
    const requestSettings = yield all([
      {
        url: `/filter/${EdsEntityCodes.ClaimEntity}?${paginationParams}`,
        body: [],
      },
      {
        url: `/filter/${EdsEntityCodes.ClaimEntity}?${paginationParams}`,
        body: [
          {
            propertyName: 'AssigneeId',
            values: [userId],
          },
        ],
      },
      {
        url: `/filter/${EdsEntityCodes.ClaimSet}?${paginationParams}`,
        body: [],
      },
      {
        url: `/filter/${EdsEntityCodes.ClaimSet}?${paginationParams}`,
        body: [
          {
            propertyName: 'AssigneeId',
            values: [userId],
          },
        ],
      },
    ]);

    const [
      {
        data: {
          pagination: { numberOfItems: allClaimsCounter },
        },
      },
      {
        data: {
          pagination: { numberOfItems: myClaimsCounter },
        },
      },
      {
        data: {
          pagination: { numberOfItems: allClaimSetsCounter },
        },
      },
      {
        data: {
          pagination: { numberOfItems: myClaimSetsCounter },
        },
      },
    ] = yield all(
      requestSettings.map((request) =>
        call(HttpService.post, request.url, request.body, { baseURL: import.meta.env.VITE_API_URL_EDS }),
      ),
    );

    yield put(updateAllClaimsCountersSuccess({ allClaims: allClaimsCounter || 0, myClaims: myClaimsCounter || 0 }));
    yield put(
      updateAllClaimSetsCountersSuccess({
        allClaimSets: allClaimSetsCounter || 0,
        myClaimSets: myClaimSetsCounter || 0,
      }),
    );
  } catch (error) {
    console.error(error);
  }
}

function* getAllClaimsListSaga({ type, payload }: PayloadAction<GetAllClaimsPayload>) {
  yield delay(100);

  try {
    const pagination = yield select((state: RootState) => state.allClaimsListSlice.pagination);
    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?includes=TaskEntities&${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, sortBy: payload?.sorting?.propertyName, sortDirection: payload?.sorting?.sortDirection, includes: `${EdsEntityCodes.HelperTags}` })}`;

    const { data }: HttpServiceResponse<AllClaimsListResponse> = yield call(
      HttpService.post,
      requestUrl,
      payload?.filters || [],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizeConfig: Partial<EDSAdapterServiceNormalizeConfiguration> = { includeId: true };

    if (payload.section) normalizeConfig.section = payload.section;

    const normalizedResponse: NormalizedV2Response[] = yield call(
      EdsAdapterService.normalize,
      data.listings,
      normalizeConfig,
    );

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => {
      return {
        ...normalizedClaim,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
          [EdsEntityCodes.TaskEntities]: (
            normalizedClaim.entities[EdsEntityCodes.TaskEntities] as NormalizedV2Response[]
          )?.map((taskEntity) => ({
            ...taskEntity,
            entities: RecordService.omit([EdsEntityCodes.AvailableHelperTags], taskEntity.entities),
          })),
        },
      };
    });

    EdsDebugService.logResponse(EdsEntityCodes.ClaimEntity, {
      section: payload?.section,
      rawResponse: data,
      normalizedResponse: claimsWithoutAvailableHelperTags,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<AllClaimSetsSlots>(normalizedResponse[0])
        : {},
    });

    const counters: { allClaims?: number; myClaims?: number } = {};
    if (payload.type === MyClaimsTabs.AllClaims) counters.allClaims = data.pagination.numberOfItems;
    else if (payload.type === MyClaimsTabs.MyClaims) counters.myClaims = data.pagination.numberOfItems;
    else if (payload?.location && payload?.customInboxId)
      yield put(
        updateCustomInboxCounter({
          id: payload.customInboxId,
          location: payload.location,
          counter: data.pagination.numberOfItems,
        }),
      );

    yield put(
      getAllClaimsListSuccess({
        shouldRewrite: type === getAllClaimsList.type,
        counters,
        listings: claimsWithoutAvailableHelperTags,
        pagination: {
          ...data.pagination,
          isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
        },
      }),
    );
  } catch (error) {
    console.error(error);
    yield put(resetAllClaimsListSlice);
  }
}

export function* allClaimsWatcher() {
  yield takeLatest([getAllClaimsList.type, incrementAllClaimsListPage.type], getAllClaimsListSaga);
  yield takeLatest([updateAllClaimsCounters.type], updateMyClaimsCountersSaga);
}
